import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import { Box, Menu, MenuItem, styled } from "@mui/material"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { makeStyles } from "tss-react/mui-compat"
import { ConventionRegistrationState } from "app-constants/constants"
import LaunchpadTicketsCount from "components/Launchpad/LaunchpadTicketsCount"
import ShowTicketsDialog from "components/conventions/ShowTicketsDialog"
import FewSpotsLeft from "components/conventions/registration/FewSpotsLeft"
import { useConventionRegistration } from "contexts/ConventionRegistrationContext"
import { useBoolean } from "hooks/useBoolean"
import React, { useId, useState } from "react"
import { LoadingIndicator } from "stream-chat-react"
import { pxToRem } from "utils/helpers"
import Convention from "models/convention-api/v1/Convention"
import useCurrent from "lib/use-current"
import { ActivityIndicator } from "components/activity-indicator"

const useStyles = makeStyles()(
  (theme) => ({
    registeredClosed: {
      ...theme.fonts.lato400,
      fontSize: pxToRem(16),
      lineHeight: 1.375,
      fontStyle: "italic",
      color: "#FFFFFF",
      marginTop: "auto",
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.down("md")]: {
        marginTop: "30px",
        alignSelf: "center",
      },
    },
    registering: {
      fontFamily: "Lato",
      fontStyle: "italic",
      fontWeight: "normal",
      fontSize: "16px",
      lineHeight: "19px",
      color: "#FFFFFF",
      marginTop: "auto",
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.down("md")]: {
        marginTop: "30px",
        alignSelf: "center",
      },
    },
    button: {
      color: theme.palette.gray.darkest,
      backgroundColor: theme.palette.gray.lightest,
      marginTop: "auto",
      maxWidth: "268px",

      "&:hover, &:active": {
        backgroundColor: theme.palette.gray.main,
      },

      [theme.breakpoints.down("md")]: {
        maxWidth: "100%",
        width: "100%",
        marginTop: "30px",
      },
    },
    buttonText: {
      textTransform: "uppercase",
    },
    progressIcon: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translateY(-50%) translateX(-50%)",
    },
  })
)

interface LaunchpadRegisterButtonProps {}

export const LaunchpadRegisterButton = (props: LaunchpadRegisterButtonProps) => {
  const dropdownMenuId = useId()
  const {} = props
  const conventionRegistration = useConventionRegistration()
  const { classes } = useStyles()
  const showTicketsDialogOpen = useBoolean(false)
  const current = useCurrent()
  const userConventionRegistrantTypeId = current?.user?.convention_registrant_type_id

  const conventionsAvailableForRegistrationByRegistrantType = Convention.useAll({
    filter: {
      id: conventionRegistration.convention.id as string,
      upcoming: "true",
      ...(userConventionRegistrantTypeId && {
        convention_registrant_type_id: [userConventionRegistrantTypeId, "all"],
      }),
    },
  })

  const registrantTypeCheckIsLoading = (!conventionsAvailableForRegistrationByRegistrantType.data && conventionsAvailableForRegistrationByRegistrantType.isValidating) || conventionsAvailableForRegistrationByRegistrantType.isValidating

  const [dropdownMenuAnchorElement, setDropdownMenuAnchorElement] = useState<null | HTMLElement>(
    null,
  )
  const dropdownMenuOpen = Boolean(dropdownMenuAnchorElement)

  const onOpenDropdown = (event: React.MouseEvent<HTMLElement>) =>
    setDropdownMenuAnchorElement(event.currentTarget)

  const onCloseDropdown = () => setDropdownMenuAnchorElement(null)

  const handleDropdownMenuClick = (callback: () => void) => () => {
    setDropdownMenuAnchorElement(null)
    callback()
  }

  if (conventionRegistration.state === ConventionRegistrationState.PROCESSING_PAYMENT) {
    return <Typography className={classes.registeredClosed}>Processing payment.</Typography>
  }

  if (conventionRegistration.state === ConventionRegistrationState.REGISTERED_PAID) {
    return (
      <_RegisteredContainer>
        <Button
          sx={{ width: "100%", maxWidth: pxToRem(268), minWidth: pxToRem(200) }}
          variant="contained"
          color="light"
          onClick={showTicketsDialogOpen.on}
          aria-label={`Show the tickets for ${conventionRegistration.convention.name}`}>
          Show tickets
        </Button>
        <_LaunchpadTicketsCount />
        {showTicketsDialogOpen.value && (
          <ShowTicketsDialog
            onClose={showTicketsDialogOpen.off}
            convention={conventionRegistration.convention}
            conventionRegistration={conventionRegistration.conventionRegistration!}
          />
        )}
      </_RegisteredContainer>
    )
  }

  if (conventionRegistration.state === ConventionRegistrationState.REGISTERED) {
    return (
      <_RegisteredContainer>
        <Typography variant="paragraphItalic">You’re registered.</Typography>
        <_DropdownContainer>
          <_LaunchpadTicketsCount />
          <_DropdownMenuButton
            aria-label="Open registration dropdown menu"
            id={dropdownMenuId}
            aria-controls={dropdownMenuOpen ? dropdownMenuId : undefined}
            aria-haspopup="true"
            aria-expanded={dropdownMenuOpen ? "true" : undefined}
            onClick={onOpenDropdown}>
            <ArrowDropDownIcon color="inherit" />
          </_DropdownMenuButton>
          <_Menu
            id={dropdownMenuId}
            aria-labelledby={dropdownMenuId}
            anchorEl={dropdownMenuAnchorElement}
            open={dropdownMenuOpen}
            onClose={onCloseDropdown}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}>
            <_MenuItem
              onClick={handleDropdownMenuClick(() => conventionRegistration.openCancelDialog())}>
              Cancel Registration
            </_MenuItem>
          </_Menu>
        </_DropdownContainer>
      </_RegisteredContainer>
    )
  }

  if (conventionRegistration.state === ConventionRegistrationState.CLOSED) {
    return (
      <Typography className={classes.registeredClosed}>
        Registration for this event has been closed.
      </Typography>
    )
  }

  if (conventionRegistration.state === ConventionRegistrationState.AT_CAPACITY) {
    return (
      <Typography className={classes.registeredClosed}>
        There are no spots available for this event.
      </Typography>
    )
  }

  if (registrantTypeCheckIsLoading) {
    return <ActivityIndicator />
  }

  const conventionAvailableForRegistrationByRegistrantType = conventionsAvailableForRegistrationByRegistrantType?.data?.[0]
  if (!conventionAvailableForRegistrationByRegistrantType) {
    return (
      <Typography className={classes.registeredClosed}>
        This event is not available for you to register.
      </Typography>
    )
  }


  return (
    <Box display="flex" alignItems="center" flexWrap="nowrap" gap={pxToRem(20)}>
      <Button
        color="inherit"
        disabled={conventionRegistration.state === ConventionRegistrationState.LOADING}
        className={classes.button}
        variant="contained"
        onClick={() => conventionRegistration.openRegisterDialog()}>
        <span className={classes.buttonText}>Register</span>
        {conventionRegistration.state === ConventionRegistrationState.LOADING && (
          <div className={classes.progressIcon}>
            <LoadingIndicator />
          </div>
        )}
      </Button>
      {conventionRegistration.state === ConventionRegistrationState.FEW_SPOTS_LEFT && (
        <_FewSpotsLeft />
      )}
    </Box>
  )
}

const _RegisteredContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexFlow: "nowrap row",

  marginTop: "30px",
  alignSelf: "center",

  color: theme.palette.gray.lightest,

  [theme.breakpoints.up("md")]: {
    alignSelf: "initial",
    marginTop: "auto",
    paddingTop: pxToRem(20),
  },
})) as typeof Box

const _DropdownContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  flexFlow: "nowrap row",
})) as typeof Box

const _DropdownMenuButton = styled(IconButton)(({ theme }) => ({
  marginLeft: pxToRem(10),
  color: theme.palette.gray.lightest,
})) as unknown as typeof IconButton

const _Menu = styled(Menu)(({ theme }) => ({
  ".MuiPaper-root": {
    minWidth: pxToRem(220),
    background: theme.palette.gray.darkest,
  },
})) as typeof Menu

const _MenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.typography.paragraphRegular,
  color: theme.palette.gray.lightest,
  padding: `${pxToRem(7.5)} ${pxToRem(15)}`,
})) as unknown as typeof MenuItem

const _FewSpotsLeft = styled(FewSpotsLeft)(({ theme }) => ({
  color: theme.palette.gray.lightest,
})) as typeof FewSpotsLeft

const _LaunchpadTicketsCount = styled(LaunchpadTicketsCount)(() => ({
  marginLeft: pxToRem(20),
})) as typeof LaunchpadTicketsCount
